/**
 * How often the Rückläufer tab re-reads the process state.
 *
 * decredo answers an action with a short acknowledgement and finishes the step asynchronously, so
 * the new mask only ever becomes visible through a later read. The question is how fast to look,
 * and the honest answer comes from `waitingFor`, not from `status`.
 *
 * `status` cannot answer it: `Running` buckets ten internal states, so a process decredo is
 * actively working on looks exactly like one parked on a verify+/DEKRA webhook or on a human
 * approver — waits that legitimately last minutes or hours and must not be polled once a second.
 * `waitingFor` splits that bucket by who has to act: only `Engine` means the next halt is coming
 * on its own, and that is what `engineIsWorking` carries in here.
 *
 * `transitionWindowMs` is therefore no longer the mechanism, only a safety net: a process wedged
 * in `Engine` must not be polled fast forever.
 */

export const idleIntervalMs = 15_000;
export const transitionIntervalMs = 1_000;

/**
 * Upper bound for the fast phase, in case `waitingFor` stays `Engine` longer than a step can
 * plausibly take. Generous enough to cover a step running an API-call node (decredo's own adapters
 * allow ~30 s).
 */
export const transitionWindowMs = 30_000;

/** The three wire values that mean the process will not move again on its own. */
const terminalStatuses = new Set(['Completed', 'Failed', 'Cancelled']);

export interface RefetchIntervalInput {
  /** decredo's process status, or undefined when nothing is loaded / no process runs. */
  status: string | undefined;
  /**
   * Whether decredo's own engine is the one holding the process.
   *
   * A boolean rather than the ProcessWaitingFor value: importing the generated enum for its runtime
   * value would pull ApiClient.ts — and through it the whole application — into this pure module.
   * The comparison against the generated enum happens in useReturnProcess, where that import is
   * already safe and the compiler checks the member name.
   */
  engineIsWorking: boolean;
  /** Correlation key of the halt currently shown; undefined while decredo is between halts. */
  currentCorrelationKey: string | undefined;
  /** Correlation key the last action was sent for; undefined when no action is outstanding. */
  sentCorrelationKey: string | undefined;
  /** Timestamp (ms) until which the fast phase may last. */
  burstUntil: number;
  /** Current time (ms). Injected so this stays a pure function. */
  now: number;
}

export function returnProcessRefetchInterval({
  status,
  engineIsWorking,
  currentCorrelationKey,
  sentCorrelationKey,
  burstUntil,
  now,
}: RefetchIntervalInput): number {
  // Nothing loaded, or no process for this contract: never burst.
  if (status === undefined) return idleIntervalMs;

  // No action outstanding, or the safety net has run out.
  if (now >= burstUntil) return idleIntervalMs;

  // The process reached its end as a result of the action — there is no next halt to wait for.
  if (terminalStatuses.has(status)) return idleIntervalMs;

  // The decisive signal: anything other than the engine working means someone else has to act
  // first, and that wait is not measured in seconds.
  if (!engineIsWorking) return idleIntervalMs;

  // Backstop for a stale halt still attached while decredo reports Engine: a halt other than the
  // one we acted on means the step we were waiting for has already arrived.
  if (currentCorrelationKey !== undefined && currentCorrelationKey !== sentCorrelationKey) {
    return idleIntervalMs;
  }

  return transitionIntervalMs;
}
